// Pure derivation helpers for the PC Monitor candidate list (CHO-706).
// Kept fetch-free so both the page components and the server-side node:test
// suite can import them.

import type { PcMonitorListing, PcMonitorPriceHistoryEntry } from '../../types/pcmonitor.ts';

export interface ValueGap {
  /** estimated_value − asking_price, in the listing's currency. */
  gap: number;
  /** Gap as a percentage of estimated_value, one decimal. */
  gapPct: number;
}

/**
 * Value gap — the machine-well-under-its-parts signal this page exists to
 * surface. No number is ever shown (and no gap computed) when the asking
 * price is unresolved (CHO-698) or the evaluator produced no estimate.
 */
export function computeValueGap(
  askingPrice: number | null,
  estimatedValue: number | null,
): ValueGap | null {
  if (askingPrice == null || estimatedValue == null) return null;
  const gap = estimatedValue - askingPrice;
  return { gap, gapPct: estimatedValue > 0 ? Math.round((gap / estimatedValue) * 1000) / 10 : 0 };
}

export function listingValueGap(listing: PcMonitorListing): ValueGap | null {
  if (!listing.price_resolved) return null;
  return computeValueGap(listing.asking_price, listing.estimated_value);
}

export type ValueGapUnavailable = 'unresolved_price' | 'no_estimate';

/**
 * Why no gap can be shown, when it can't — the two null-reasons of
 * listingValueGap pinned apart. An unresolved asking price (CHO-698) is a
 * price problem; a resolved price the evaluator could not value is not,
 * and the UI must never relabel one as the other.
 */
export function listingValueGapUnavailable(listing: PcMonitorListing): ValueGapUnavailable | null {
  if (listingValueGap(listing) !== null) return null;
  if (!listing.price_resolved || listing.asking_price == null) return 'unresolved_price';
  return 'no_estimate';
}

/**
 * Price drop: the latest observed price is below an earlier one. Returns the
 * previous high and the drop size, or null when the price never fell. An old
 * listing whose price just dropped is the strongest buy signal in this data.
 */
export function latestPriceDrop(history: PcMonitorPriceHistoryEntry[]): { from: number; drop: number } | null {
  if (history.length < 2) return null;
  const latest = history[history.length - 1];
  const previousHigh = Math.max(...history.slice(0, -1).map((e) => e.price));
  if (latest.price >= previousHigh) return null;
  return { from: previousHigh, drop: previousHigh - latest.price };
}

/** Whole days since first_seen, or null when the timestamp is unparseable. */
export function daysSince(iso: string, now: Date = new Date()): number | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}

/**
 * Default sort: value gap descending. Rows without a computable gap
 * (unresolved price, no estimate) sink to the bottom — never interleaved
 * with real gaps — but keep a stable relative order.
 */
export function sortByValueGap(listings: PcMonitorListing[]): PcMonitorListing[] {
  return [...listings].sort((a, b) => {
    const ga = listingValueGap(a);
    const gb = listingValueGap(b);
    if (ga && gb) return gb.gap - ga.gap;
    if (ga) return -1;
    if (gb) return 1;
    // No computable gap on either side: an unresolved price (CHO-698) sinks
    // below a resolved price with no estimate — a real number outranks none.
    if (!a.price_resolved && !b.price_resolved) return 0;
    if (!a.price_resolved) return 1;
    if (!b.price_resolved) return -1;
    return 0;
  });
}

export function formatChf(amount: number): string {
  return `CHF ${amount.toLocaleString('de-CH')}`;
}
